import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { cx } from 'linaria';

import { getStocksInStores, getIsGoodSupplyLoading } from 'features/goods-supply';
import { analyticsUtils } from 'utils';
import {
  yandexSplitContainer,
  additionalMarginTop,
  additionalMarginBottom,
  yandexSplitWidget,
  hidden,
} from './styles';
import { YandexSplitBannerSkeleton } from './yandex-split-banner-skeleton';
import { useBannerMeta, useDebouncedQuantity, useYandexPayWidget } from './hooks';
import { getVisibilityBanner } from './utils';
import { getIsBot } from 'common/selectors';
import { getCurrency, getDivisionId, getSource } from 'features/product-info';
import { YaSplitInfo } from 'types/page-model';

const DATA_QA_YANDEXSPLIT = 'yandexsplit_mf-pdp';

type Props = {
  yaSplitInfo: YaSplitInfo;
  price?: number | null;
  isSet?: boolean;
  showCatchPushPromo?: boolean;
  quantity: number;
};

export const YandexSplitBanner: React.FC<Props> = ({
  yaSplitInfo,
  price,
  isSet,
  showCatchPushPromo,
  quantity,
}) => {
  const [isWidgetMounted, setIsWidgetMounted] = useState(false);

  const isSentAnalytics = useRef(false);

  const isBot = useSelector(getIsBot);
  const currentDivision = String(useSelector(getDivisionId));
  const currentSource = useSelector(getSource)?.toLowerCase();
  const currency = useSelector(getCurrency) || 'RUB';
  const stocks = useSelector(getStocksInStores);
  const isStocksLoading = useSelector(getIsGoodSupplyLoading);

  const { isCalcAvailable, isShowBoosterButton } = useBannerMeta();

  const debouncedQuantity = useDebouncedQuantity(quantity, setIsWidgetMounted);

  const shouldShowBanner = getVisibilityBanner({
    isBot,
    enabled: yaSplitInfo.enabled,
    stocks,
    isStocksLoading,
    isSet,
    price,
    maxPriceThreshold: yaSplitInfo.maxPriceThreshold,
    KindDivisionsList: yaSplitInfo.KindDivisionsList,
    currentDivision,
    KindSourceList: yaSplitInfo.KindSourceList,
    currentSource,
  });

  if (shouldShowBanner && !isStocksLoading && !isSentAnalytics.current) {
    analyticsUtils.sendEvent('GA:show', {
      elementName: 'Яндекс.Сплит',
      elementText: 'Оплата частями',
      elementType: 'banner',
      elementBlock: isSet ? 'komplektyDetail' : 'productDetail',
    });
    isSentAnalytics.current = true;
  }

  const widgetRef = useYandexPayWidget({
    merchantId: yaSplitInfo.merchantId,
    price,
    debouncedQuantity,
    env: yaSplitInfo.env,
    version: yaSplitInfo.version,
    currency,
    availablePaymentMethods: yaSplitInfo.availablePaymentMethods,
    setIsWidgetMounted,
    stocks,
    hideWidgetHeader: yaSplitInfo.hideWidgetHeader,
  });

  const containerClassName = cx(
    yandexSplitContainer,
    isSet ? additionalMarginTop : null,
    (isCalcAvailable || isShowBoosterButton) && !showCatchPushPromo ? additionalMarginBottom : null,
  );

  if (!shouldShowBanner) return null;

  return (
    <div className={!stocks.length ? hidden : containerClassName} data-qa={DATA_QA_YANDEXSPLIT}>
      <div ref={widgetRef} className={yandexSplitWidget} id="yapay-widget" />
      {!isWidgetMounted && <YandexSplitBannerSkeleton />}
    </div>
  );
};
